export const SUPPORTED_MIME_TYPES = {
  docs: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  images: ['image/jpeg', 'image/png', 'image/heif', 'image/heic'],
  videos: ['video/mp4', 'video/mpeg', 'video/quicktime'],
}

export const getBlobFromUri = async (uri: string): Promise<Blob> => {
  const response = await fetch(uri)
  return response.blob()
}

export const getFilenameFromBlob = (blob: Blob): string => {
  const extension = blob.type.split('/')[1] // e.g., 'jpeg', 'png'
  return `${Date.now()}.${extension}`
}

/**
 * Re-encodes a blob to JPEG, leaving one that already is untouched.
 *
 * Capacitor hands back JPEG on native, but the web file picker returns whatever
 * the user selected. Endpoints that accept JPEG only need the conversion, and
 * doing it here also strips EXIF, including any GPS tag the camera wrote.
 *
 * Throws when the browser cannot decode the source, which is what happens to a
 * HEIC picked from disk in Chrome.
 */
export const toJpegBlob = async (blob: Blob, quality = 0.8): Promise<Blob> => {
  if (blob.type === 'image/jpeg') {
    return blob
  }

  const bitmap = await createImageBitmap(blob).catch(() => {
    throw new Error(`Unsupported image format: ${blob.type || 'unknown'}`)
  })

  try {
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0)

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (converted) => (converted ? resolve(converted) : reject(new Error('Could not encode the image as JPEG'))),
        'image/jpeg',
        quality,
      )
    })
  } finally {
    bitmap.close()
  }
}
